import { useCallback, useEffect, useState } from 'react'
import { mergePublic } from '../services/store'
import { fetchPublicLanding } from '../services/landingService'
import { fetchPublicForm, fetchPublicFormById } from '../services/formService'
import { fetchPublicOrganization } from '../services/organizationService'

/**
 * Estado de carga de una página pública.
 *
 * `cargando` es el estado inicial y nunca debe mostrar «esta página no existe»:
 * el 404 solo corresponde a `no_encontrada`, es decir, cuando la consulta ya
 * terminó correctamente y no hay ninguna landing publicada con ese slug.
 * Un fallo de red (habitual al abrir el enlace desde WhatsApp en el móvil)
 * devuelve `error`, que se reintenta solo una vez y ofrece «Reintentar».
 */
export type PublicStatus = 'cargando' | 'lista' | 'no_encontrada' | 'error'

export interface PublicContentState {
  status: PublicStatus
  /** Mensaje real del fallo, solo cuando `status` es `error`. */
  message?: string
  retry: () => void
}

const RETRY_DELAY = 1200

/**
 * Descarga una landing publicada (y su formulario y marca) para un visitante
 * anónimo, y la vuelca en la caché para que las vistas la lean igual que
 * cuando hay sesión iniciada.
 */
export function usePublicLanding(slug: string): PublicContentState {
  const [status, setStatus] = useState<PublicStatus>('cargando')
  const [message, setMessage] = useState<string>()
  const [attempt, setAttempt] = useState(0)

  const retry = useCallback(() => setAttempt((n) => n + 1), [])

  useEffect(() => {
    let active = true
    let timer: number | undefined
    let autoRetried = false
    setStatus('cargando')
    setMessage(undefined)

    async function load(): Promise<void> {
      const landing = await fetchPublicLanding(slug)
      if (!active) return
      if (!landing) {
        setStatus('no_encontrada')
        return
      }
      // El formulario y la marca son complementarios: si fallan, la landing se
      // muestra igualmente en lugar de convertirse en un falso 404.
      const [form, organization] = await Promise.all([
        landing.formId
          ? fetchPublicFormById(landing.formId).catch(() => null)
          : null,
        fetchPublicOrganization(landing.organizationId).catch(() => null),
      ])
      if (!active) return
      mergePublic({
        landings: [landing],
        forms: form ? [form] : [],
        organizations: organization ? [organization] : [],
      })
      setStatus('lista')
    }

    function fail(error: unknown): void {
      if (!active) return
      if (!autoRetried) {
        // Reintento único: cubre la red inestable del primer toque en móvil.
        autoRetried = true
        timer = window.setTimeout(() => {
          load().catch(fail)
        }, RETRY_DELAY)
        return
      }
      setMessage(error instanceof Error ? error.message : undefined)
      setStatus('error')
    }

    load().catch(fail)

    return () => {
      active = false
      if (timer) window.clearTimeout(timer)
    }
  }, [slug, attempt])

  return { status, message, retry }
}

/** Descarga un formulario público activo por su slug. */
export function usePublicForm(slug: string): PublicContentState {
  const [status, setStatus] = useState<PublicStatus>('cargando')
  const [message, setMessage] = useState<string>()
  const [attempt, setAttempt] = useState(0)

  const retry = useCallback(() => setAttempt((n) => n + 1), [])

  useEffect(() => {
    let active = true
    let timer: number | undefined
    let autoRetried = false
    setStatus('cargando')
    setMessage(undefined)

    async function load(): Promise<void> {
      const form = await fetchPublicForm(slug)
      if (!active) return
      if (!form) {
        setStatus('no_encontrada')
        return
      }
      const organization = await fetchPublicOrganization(
        form.organizationId,
      ).catch(() => null)
      if (!active) return
      mergePublic({
        forms: [form],
        organizations: organization ? [organization] : [],
      })
      setStatus('lista')
    }

    function fail(error: unknown): void {
      if (!active) return
      if (!autoRetried) {
        autoRetried = true
        timer = window.setTimeout(() => {
          load().catch(fail)
        }, RETRY_DELAY)
        return
      }
      setMessage(error instanceof Error ? error.message : undefined)
      setStatus('error')
    }

    load().catch(fail)

    return () => {
      active = false
      if (timer) window.clearTimeout(timer)
    }
  }, [slug, attempt])

  return { status, message, retry }
}
